import React, { useMemo, useEffect } from "react";
import moment from "moment";
import ServeIQLoader from "../preLoad/serveiqLoader";
import {
  buildOrderTree,
  getMenuItemName,
  getMenuItemQty,
  getMenuItemPrice,
  getSubTotal,
  getMenuItemIsModifier,
  getNestedModifierOptionNodes,
  getModifierChildNodes,
  isModifierLine,
  isVariantType,
  isDealOrComboHeader,
  getRecvOrderDetailsList,
  normalizeType,
} from "../../../utils/orderDetailsTree";
import "./OrderDetailsModal.scss";

const BODY_LOCK_CLASS = "order-details-modal-open";

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

function getCurrencySymbol(currencyCode) {
  return currencySymbols[currencyCode] || currencyCode || "";
}

function MetaRow({ label, children }) {
  if (children == null || children === "") return null;
  return (
    <div className="order-details-modal__meta-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function StatusChip({ tone = "neutral", children }) {
  return (
    <span className={`order-details-modal__chip order-details-modal__chip--${tone}`}>
      {children}
    </span>
  );
}

function Money({ currency, amount }) {
  return (
    <>
      {getCurrencySymbol(currency)}
      {Number(amount || 0).toFixed(2)}
    </>
  );
}

/** Amount charged on a modifier row, matching what ModifierRow prints. */
function getModifierRowTotal(modNode) {
  const nested = getNestedModifierOptionNodes(modNode);
  if (nested.length > 0) {
    return nested.reduce((sum, child) => sum + getModifierRowTotal(child), 0);
  }
  const modifier = modNode?.item || {};
  const discount = parseFloat(modifier?.discountAmount ?? modifier?.DiscountAmount ?? 0) || 0;
  return (
    getSubTotal(modifier) ||
    Math.max(0, getMenuItemQty(modifier) * getMenuItemPrice(modifier) - discount)
  );
}

function ModifierRow({ modNode, depth = 0, currency }) {
  const modifier = modNode?.item || {};
  const nested = getNestedModifierOptionNodes(modNode);
  if (nested.length > 0) {
    return (
      <div
        key={modNode.id ?? `${getMenuItemName(modifier)}-group-${depth}`}
        className="order-details-modal__mod-group"
        style={{ paddingLeft: depth * 10 }}
      >
        <div className="order-details-modal__mod-group-title">
          {getMenuItemName(modifier)}
        </div>
        {nested.map((child) => (
          <ModifierRow
            key={child.id ?? getMenuItemName(child.item)}
            modNode={child}
            depth={depth + 1}
            currency={currency}
          />
        ))}
      </div>
    );
  }

  const modifierQty = getMenuItemQty(modifier);
  // Deal modifiers carry no SubTotal (their cost is inside the deal header), so fall back to the
  // line's own charged amount.
  const modifierTotal =
    getSubTotal(modifier) ||
    Math.max(
      0,
      modifierQty * getMenuItemPrice(modifier) -
        (parseFloat(modifier?.discountAmount ?? modifier?.DiscountAmount ?? 0) || 0)
    );
  const showPlus =
    getMenuItemIsModifier(modifier) ||
    normalizeType(modifier.menuItem_Type ?? modifier.MenuItem_Type) === "MODIFIER";

  return (
    <div
      className="order-details-modal__line order-details-modal__line--mod"
      style={{ paddingLeft: depth * 10 }}
    >
      <span className="order-details-modal__qty">{modifierQty}×</span>
      <span className="order-details-modal__name">
        {showPlus ? "+ " : ""}
        {getMenuItemName(modifier)}
      </span>
      <span className="order-details-modal__price">
        <Money currency={currency} amount={modifierTotal} />
      </span>
    </div>
  );
}

function OrderItemNode({ node, currency }) {
  const item = node?.item || {};
  if (isModifierLine(item)) return null;

  const itemType = normalizeType(item.menuItem_Type ?? item.MenuItem_Type);
  const variants = (node.children || []).filter((c) => isVariantType(c.item));
  const modifiers = getModifierChildNodes(node).filter((c) => !isVariantType(c.item));
  const childLines = (node.children || []).filter((c) => !isModifierLine(c.item));
  const isComboItem = itemType === "COMBO" || itemType.includes("COMBO");
  // Deal slot picks carry no SubTotal — the package price is on the deal header — so show the
  // deal-applied price (catalogue price less the discount stored on the line).
  const ownTotal =
    getSubTotal(item) ||
    Math.max(
      0,
      getMenuItemQty(item) * getMenuItemPrice(item) -
        (parseFloat(item?.discountAmount ?? item?.DiscountAmount ?? 0) || 0)
    );
  // A line is billed as its own price plus its add-on rows, so show that as the line total —
  // for plain items and for deal slot picks, whose picks then add up to the deal price. The
  // deal header is excluded because its total already covers every add-on inside the package.
  const rollsUpModifiers = !isDealOrComboHeader(item);
  const lineTotal = rollsUpModifiers
    ? modifiers.reduce((sum, m) => sum + getModifierRowTotal(m), ownTotal)
    : ownTotal;
  const variantLabel = variants.map((v) => getMenuItemName(v.item)).filter(Boolean).join(", ");
  const itemLabel = variantLabel
    ? `${getMenuItemName(item)} (${variantLabel})`
    : getMenuItemName(item);

  return (
    <div className="order-details-modal__item" key={node.id ?? getMenuItemName(item)}>
      <div className="order-details-modal__line order-details-modal__line--item">
        <span className="order-details-modal__qty">{getMenuItemQty(item)}×</span>
        <div className="order-details-modal__name-block">
          <div className="order-details-modal__name">{itemLabel}</div>
        </div>
        <span className="order-details-modal__price order-details-modal__price--strong">
          <Money currency={currency} amount={lineTotal} />
        </span>
      </div>

      {modifiers.length > 0 && (
        <div className="order-details-modal__mods">
          <div className="order-details-modal__mods-label">
            {isComboItem ? "Includes" : "Modifiers"}
          </div>
          {modifiers.map((m) => (
            <ModifierRow
              key={m.id ?? getMenuItemName(m.item)}
              modNode={m}
              currency={currency}
            />
          ))}
        </div>
      )}

      {childLines.map((child) => (
        <OrderItemNode
          key={child.id ?? getMenuItemName(child.item)}
          node={child}
          currency={currency}
        />
      ))}
    </div>
  );
}

/**
 * Modern order details panel for Manage Order (replaces thermal-receipt modal).
 */
export default function OrderDetailsModal({
  open,
  order,
  detailedOrder,
  customerDetails,
  loading = false,
  currency,
  isDarkMode = false,
  getDisplayPaymentMethod,
  isFullRefundStatus,
  isPartialRefundStatus,
  onClose,
}) {
  const active = detailedOrder || order;

  const paymentLabel = useMemo(() => {
    const status = String(active?.recvorder_Paymentrecvstatus || "").toLowerCase();
    return status === "completed" ? "Paid" : "Unpaid";
  }, [active]);

  const paymentTone = paymentLabel === "Paid" ? "success" : "warning";

  const orderTypeLabel = useMemo(() => {
    const orderType = active?.recvorder_Ordertype;
    const tableName = detailedOrder?.t_name || order?.t_Name;
    if (orderType === "Dine-In" && tableName) return `${orderType} · ${tableName}`;
    return orderType || "N/A";
  }, [active, detailedOrder, order]);

  const dateLabel = useMemo(() => {
    const raw = detailedOrder?.recvorder_datetime || order?.recvorder_datetime;
    if (raw && moment(raw).isValid()) return moment(raw).format("DD MMM YYYY · h:mm A");
    return "N/A";
  }, [detailedOrder, order]);

  const frontOrderId =
    detailedOrder?.recvorder_FrontOrderID || order?.recvorder_FrontOrderID || "—";
  const invoiceNo =
    detailedOrder?.recvorder_InvoiceNo || order?.recvorder_InvoiceNo || "N/A";
  const deliveryAddress =
    detailedOrder?.recvOrder_DeliveryAddress ||
    order?.recvOrder_DeliveryAddress ||
    customerDetails?.Cust_Address ||
    "";
  const isDelivery = (detailedOrder?.recvorder_Ordertype || order?.recvorder_Ordertype) === "Delivery";
  const showAddress = isDelivery || Boolean(deliveryAddress);

  const orderTypeNormalized = String(
    detailedOrder?.recvorder_Ordertype || order?.recvorder_Ordertype || ""
  )
    .toLowerCase()
    .trim();
  const isCollectionOrPickup =
    orderTypeNormalized === "collection" ||
    orderTypeNormalized === "pickup" ||
    orderTypeNormalized === "takeaway";
  const pickExpectedTime = (...keys) => {
    const sources = [detailedOrder, order];
    for (const src of sources) {
      if (!src) continue;
      for (const key of keys) {
        const value = src[key];
        if (value != null && String(value).trim() !== "") return String(value).trim();
      }
    }
    return "";
  };
  const expectedDateTime = isCollectionOrPickup
    ? pickExpectedTime(
        "recvorder_ExpectedCollectionTime",
        "Recvorder_ExpectedCollectionTime",
        "recvorder_ExpectedDeliveryTime",
        "Recvorder_ExpectedDeliveryTime"
      )
    : orderTypeNormalized === "delivery"
      ? pickExpectedTime(
          "recvorder_ExpectedDeliveryTime",
          "Recvorder_ExpectedDeliveryTime"
        )
      : "";
  const expectedTimeLabel =
    orderTypeNormalized === "delivery" ? "Expected delivery" : "Expected collection";

  const itemRoots = useMemo(() => {
    const details = getRecvOrderDetailsList(detailedOrder);
    return buildOrderTree(details.length > 0 ? details : order?.items || []);
  }, [detailedOrder, order]);

  const refundUi = useMemo(() => {
    if (!active) return null;
    const paymentStatus = String(active?.recvorder_Paymentrecvstatus || "").toLowerCase();
    const isPaid = paymentStatus === "completed";
    const orderStatus = String(active?.recvorder_status || "").trim();
    const isCancelled = orderStatus === "Cancelled";
    const isRefundStatus = isFullRefundStatus?.(orderStatus);
    const isPartiallyRefunded = isPartialRefundStatus?.(orderStatus);
    const reason =
      active?.orderRejectorRefundReason ?? active?.OrderRejectorRefundReason ?? "";
    const hasRefundInfo = reason || active?.isRefunded;
    const showRefunded =
      isRefundStatus || isPartiallyRefunded || (hasRefundInfo && isPaid);

    if (showRefunded) {
      const adjList = active?.recvorderAdjustments ?? active?.RecvorderAdjustments ?? [];
      const refundTotal = Array.isArray(adjList)
        ? adjList
            .filter((a) => String(a?.type ?? a?.Type ?? "").toLowerCase() === "refund")
            .reduce((s, a) => s + (Number(a?.amount ?? a?.Amount) || 0), 0)
        : 0;
      return {
        tone: "danger",
        title: isPartiallyRefunded ? "Partially refunded" : "Order refunded",
        amountLabel: isPartiallyRefunded ? (
          <>
            Refunded so far: <Money currency={currency} amount={refundTotal} />
          </>
        ) : (
          <>
            Refunded:{" "}
            <Money
              currency={currency}
              amount={
                detailedOrder?.recvorder_Totalamount ||
                order?.grandTotal ||
                order?.recvorder_Totalamount ||
                0
              }
            />
          </>
        ),
        reason: reason || "No reason provided",
      };
    }

    if (isCancelled) {
      return {
        tone: "danger",
        title: "Order cancelled",
        amountLabel: null,
        reason: reason || "No reason provided",
      };
    }

    return null;
  }, [
    active,
    currency,
    detailedOrder,
    order,
    isFullRefundStatus,
    isPartialRefundStatus,
  ]);

  // Lock page + nested table scroll while open; only the modal body may scroll.
  useEffect(() => {
    if (!open) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.classList.add(BODY_LOCK_CLASS);

    const isInModalScrollBody = (target) => {
      const el = target instanceof Element ? target : target?.parentElement;
      return Boolean(el?.closest?.(".order-details-modal__body"));
    };

    const onWheel = (e) => {
      if (!isInModalScrollBody(e.target)) {
        e.preventDefault();
        return;
      }
      const scrollBody = e.target.closest(".order-details-modal__body");
      if (!scrollBody) {
        e.preventDefault();
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = scrollBody;
      const atTop = scrollTop <= 0 && e.deltaY < 0;
      const atBottom =
        scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atBottom) {
        e.preventDefault();
      }
    };

    const onTouchMove = (e) => {
      if (!isInModalScrollBody(e.target)) {
        e.preventDefault();
      }
    };

    document.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.classList.remove(BODY_LOCK_CLASS);
      document.removeEventListener("wheel", onWheel);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [open]);

  if (!open || !order) return null;

  const paymentMethod =
    (typeof getDisplayPaymentMethod === "function"
      ? getDisplayPaymentMethod(active)
      : active?.recvorder_Paymentmethod) || "N/A";

  return (
    <div
      className={`order-details-modal ${isDarkMode ? "order-details-modal--dark" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-details-modal-title"
    >
      <div
        className="order-details-modal__panel"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="order-details-modal__loading" role="status" aria-live="polite">
            <ServeIQLoader size={56} />
            <p>Loading order…</p>
          </div>
        )}

        <header className="order-details-modal__header">
          <div className="order-details-modal__header-text">
            <p className="order-details-modal__eyebrow">Order details</p>
            <h2 id="order-details-modal-title" className="order-details-modal__title">
              {frontOrderId}
            </h2>
            <div className="order-details-modal__chips">
              <StatusChip tone={paymentTone}>{paymentLabel}</StatusChip>
              <StatusChip>{paymentMethod}</StatusChip>
              {active?.recvorder_status ? (
                <StatusChip tone="neutral">{active.recvorder_status}</StatusChip>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="order-details-modal__close"
            onClick={onClose}
            aria-label="Close order details"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>

        <div className="order-details-modal__body">
          <section className="order-details-modal__section">
            <dl className="order-details-modal__meta">
              <MetaRow label="Invoice">{invoiceNo}</MetaRow>
              <MetaRow label="Source">
                {detailedOrder?.recvorder_ReceivedFrom ||
                  order?.recvorder_ReceivedFrom ||
                  "N/A"}
              </MetaRow>
              <MetaRow label="Type">{orderTypeLabel}</MetaRow>
              <MetaRow label="Date">{dateLabel}</MetaRow>
              {expectedDateTime ? (
                <MetaRow label={expectedTimeLabel}>{expectedDateTime}</MetaRow>
              ) : null}
              <MetaRow label="Sent by">
                {detailedOrder?.sendByName || order?.recvorder_OrderSendBy || "N/A"}
              </MetaRow>
              <MetaRow label="Customer">
                {detailedOrder?.customerName ||
                  order?.customerName ||
                  order?.customer_Name ||
                  "N/A"}
              </MetaRow>
            </dl>
          </section>

          {showAddress && (
            <section className="order-details-modal__section">
              <h3 className="order-details-modal__section-title">
                {isDelivery ? "Delivery address" : "Address"}
              </h3>
              <p className="order-details-modal__address">
                {deliveryAddress || "N/A"}
              </p>
            </section>
          )}

          {itemRoots.length > 0 && (
            <section className="order-details-modal__section">
              <div className="order-details-modal__items-head">
                <span>Qty</span>
                <span>Item</span>
                <span>Total</span>
              </div>
              <div className="order-details-modal__items">
                {itemRoots.map((root, idx) => (
                  <OrderItemNode
                    key={root.id ?? `item-root-${idx}`}
                    node={root}
                    currency={currency}
                  />
                ))}
              </div>
            </section>
          )}

          {refundUi && (
            <section
              className={`order-details-modal__alert order-details-modal__alert--${refundUi.tone}`}
            >
              <strong>{refundUi.title}</strong>
              {refundUi.amountLabel ? <div>{refundUi.amountLabel}</div> : null}
              <div className="order-details-modal__alert-reason">
                Reason: {refundUi.reason}
              </div>
            </section>
          )}
        </div>

        <footer className="order-details-modal__footer">
          <div className="order-details-modal__charges">
            <div className="order-details-modal__charge">
              <span>Discount</span>
              <span>
                <Money
                  currency={currency}
                  amount={
                    detailedOrder?.recvorder_Totaldiscount ||
                    order?.recvorder_Totaldiscount ||
                    0
                  }
                />
              </span>
            </div>
            <div className="order-details-modal__charge">
              <span>Service charges</span>
              <span>
                <Money
                  currency={currency}
                  amount={detailedOrder?.recvorder_Servicecharges || 0}
                />
              </span>
            </div>
            <div className="order-details-modal__charge">
              <span>
                VAT (
                {detailedOrder?.recvorder_VATType ||
                  order?.recvorder_VATType ||
                  "N/A"}
                )
              </span>
              <span>
                <Money currency={currency} amount={detailedOrder?.vatAmount || 0} />
              </span>
            </div>
            <div className="order-details-modal__charge">
              <span>Tip</span>
              <span>
                <Money
                  currency={currency}
                  amount={detailedOrder?.recvorder_TipAmount || 0}
                />
              </span>
            </div>
          </div>
          <div className="order-details-modal__total">
            <span>Total</span>
            <strong>
              <Money
                currency={currency}
                amount={
                  detailedOrder?.recvorder_Totalamount ||
                  order?.recvorder_Totalamount ||
                  0
                }
              />
            </strong>
          </div>
        </footer>
      </div>
    </div>
  );
}
